import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Bell, Link, User, Shield, CreditCard, HelpCircle, LogOut, Key, AlertCircle, Lock } from 'lucide-react';
import { Checkbox } from "../components/ui/checkbox";

const Settings = () => {
  const [plan, setPlan] = useState('pro');
  const [notificationTypes, setNotificationTypes] = useState({
    'ESG Alerts': false,
    'Goal Updates': false,
    'Report Generation': false,
    'Stakeholder Engagement': false,
  });

  const handleNotificationTypeChange = (type) => {
    setNotificationTypes(prev => ({
      ...prev,
      [type]: !prev[type]
    }));
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Settings</h1>
        <Button variant="outline"><HelpCircle className="mr-2 h-4 w-4" /> Help</Button>
      </div>
      
      <Tabs defaultValue="account">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="account">Account</TabsTrigger>
          <TabsTrigger value="notifications">Notifications</TabsTrigger>
          <TabsTrigger value="integrations">Integrations</TabsTrigger>
          <TabsTrigger value="billing">Billing</TabsTrigger>
          <TabsTrigger value="security">Security</TabsTrigger>
        </TabsList>
        
        <TabsContent value="account">
          <Card>
            <CardHeader>
              <CardTitle>Account Information</CardTitle>
              <CardDescription>Manage your account details and preferences.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="fullName">Full Name</Label>
                  <Input id="fullName" defaultValue="John Doe" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" type="email" defaultValue="john.doe@example.com" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="company">Company</Label>
                  <Input id="company" defaultValue="Acme Corp" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="role">Role</Label>
                  <Input id="role" defaultValue="ESG Manager" />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="bio">Bio</Label>
                <Input id="bio" defaultValue="ESG enthusiast working towards a sustainable future." />
              </div>
              <div className="space-y-2">
                <Label htmlFor="language">Preferred Language</Label>
                <Select defaultValue="en">
                  <SelectTrigger id="language">
                    <SelectValue placeholder="Select language" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="en">English</SelectItem>
                    <SelectItem value="es">Spanish</SelectItem>
                    <SelectItem value="fr">French</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button>Update Account</Button>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="notifications">
          <Card>
            <CardHeader>
              <CardTitle>Notification Preferences</CardTitle>
              <CardDescription>Manage how you receive notifications.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="emailNotifications" className="font-semibold">Email Notifications</Label>
                  <p className="text-sm text-gray-500">Receive notifications via email</p>
                </div>
                <Switch id="emailNotifications" />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="pushNotifications" className="font-semibold">Push Notifications</Label>
                  <p className="text-sm text-gray-500">Receive notifications on your device</p>
                </div>
                <Switch id="pushNotifications" />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <Label htmlFor="weeklyDigest" className="font-semibold">Weekly Digest</Label>
                  <p className="text-sm text-gray-500">Receive a summary of your ESG performance</p>
                </div>
                <Switch id="weeklyDigest" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="notificationTypes" className="font-semibold">Notification Types</Label>
                <div className="grid grid-cols-2 gap-2">
                  {Object.keys(notificationTypes).map((type) => (
                    <div key={type} className="flex items-center space-x-2">
                      <Checkbox
                        id={type}
                        checked={notificationTypes[type]}
                        onCheckedChange={() => handleNotificationTypeChange(type)}
                      />
                      <Label htmlFor={type}>{type}</Label>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="integrations">
          <Card>
            <CardHeader>
              <CardTitle>Integrations</CardTitle>
              <CardDescription>Connect your ESGrity.AI account with other services.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {[
                { name: 'Salesforce', description: 'Connect your Salesforce account', icon: Link },
                { name: 'Google Analytics', description: 'Link your Google Analytics', icon: Link },
                { name: 'Slack', description: 'Receive notifications on Slack', icon: Bell },
                { name: 'Microsoft Teams', description: 'Collaborate with your team', icon: User },
                { name: 'GitHub', description: 'Link your repositories', icon: Link },
              ].map((integration) => (
                <div key={integration.name} className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <integration.icon className="h-8 w-8 text-gray-400" />
                    <div>
                      <h3 className="font-semibold">{integration.name}</h3>
                      <p className="text-sm text-gray-500">{integration.description}</p>
                    </div>
                  </div>
                  <Button variant="outline">Connect</Button>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="billing">
          <Card>
            <CardHeader>
              <CardTitle>Billing and Subscription</CardTitle>
              <CardDescription>Manage your subscription and billing details.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <h3 className="font-semibold mb-2">Current Plan</h3>
                <div className="flex items-center space-x-2">
                  <Badge variant="secondary">{plan === 'pro' ? 'Pro' : 'Basic'}</Badge>
                  <span className="text-sm text-gray-500">
                    {plan === 'pro' ? '$49.99/month' : '$19.99/month'}
                  </span>
                </div>
              </div>
              <div>
                <h3 className="font-semibold mb-2">Payment Method</h3>
                <div className="flex items-center space-x-2">
                  <CreditCard className="h-5 w-5 text-gray-400" />
                  <span>Visa ending in 1234</span>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="plan">Change Plan</Label>
                <Select value={plan} onValueChange={setPlan}>
                  <SelectTrigger id="plan">
                    <SelectValue placeholder="Select plan" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="basic">Basic Plan</SelectItem>
                    <SelectItem value="pro">Pro Plan</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button>Update Subscription</Button>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="security">
          <Card>
            <CardHeader>
              <CardTitle>Security Settings</CardTitle>
              <CardDescription>Manage your account's security and access.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="currentPassword">Current Password</Label>
                <Input id="currentPassword" type="password" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="newPassword">New Password</Label>
                <Input id="newPassword" type="password" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm New Password</Label>
                <Input id="confirmPassword" type="password" />
              </div>
              <Button>Update Password</Button>
              <div className="pt-4 space-y-4">
                <div>
                  <h3 className="font-semibold">Two-Factor Authentication</h3>
                  <p className="text-sm text-gray-500 mb-2">Add an extra layer of security to your account</p>
                  <Button variant="outline"><Shield className="mr-2 h-4 w-4" /> Enable 2FA</Button>
                </div>
                <div>
                  <h3 className="font-semibold">Login History</h3>
                  <p className="text-sm text-gray-500 mb-2">View your recent login activity</p>
                  <Button variant="outline"><Key className="mr-2 h-4 w-4" /> View Login History</Button>
                </div>
                <div>
                  <h3 className="font-semibold">Account Deactivation</h3>
                  <p className="text-sm text-gray-500 mb-2">Temporarily deactivate your account</p>
                  <Button variant="outline" className="text-red-500 hover:text-red-700"><AlertCircle className="mr-2 h-4 w-4" /> Deactivate Account</Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      
      <Card>
        <CardContent className="pt-6">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="font-semibold">Delete Account</h3>
              <p className="text-sm text-gray-500">Permanently delete your account and all associated data</p>
            </div>
            <Button variant="destructive"><Lock className="mr-2 h-4 w-4" /> Delete Account</Button>
          </div>
        </CardContent>
      </Card>
      
      <div className="flex justify-end">
        <Button variant="outline"><LogOut className="mr-2 h-4 w-4" /> Log Out</Button>
      </div>
    </div>
  );
};

export default Settings;